<template>
  <div v-if="adMode && devMode" :style="style" class="bg-gray-600 u-center text-white text-center">
    {{ minWidth ? minWidth + 'x' + minHeight : 'Fluid' }} {{ selectedAdUnit.name }}
  </div>
  <StickyAdWrapper
    v-else-if="selectedAdUnit && isSticky && adMode"
    ref="adIntersectionRef"
    :ad-slot="slot"
    :style="style"
    :unit-name="unitName"
    @close-wrapper="clearRefreshTimer"
  >
    <div :id="outerDivId" :key="mobileKey">
      <div :id="adUnitId" class="u-center">
        <div v-if="loading && !selectedAdUnit.hideLoading" class="u-center absolute">
          <OLoading />
        </div>
      </div>
    </div>
  </StickyAdWrapper>
  <div
    v-else-if="selectedAdUnit && !selectedAdUnit.isProject && adMode"
    :id="adUnitId"
    :key="mobileKey"
    ref="adIntersectionRef"
    :style="style"
    class="u-center"
    :unit-name="unitName"
  >
    <div v-if="loading && !selectedAdUnit.hideLoading" class="u-center absolute" :style="style">
      <OLoading />
    </div>
  </div>
  <div v-else-if="selectedAdUnit && adMode" :id="outerDivId" :key="mobileKey" ref="adIntersectionRef" :style="style" :unit-name="unitName">
    <div :id="adUnitId" :style="style" class="u-center">
      <div v-if="loading && !selectedAdUnit.hideLoading" class="u-center absolute" :style="style">
        <OLoading />
      </div>
    </div>
  </div>
</template>

<script>
import {
  defineComponent,
  onMounted,
  computed,
  ref,
  onUnmounted,
  watch,
  inject,
  onBeforeMount,
  useRoute,
  useContext,
} from '@nuxtjs/composition-api';
import { adConfig } from '@/modules/ads/constants/units/ads';
import { useDevice } from '@/composables/useDevice';
import { uniqueId } from 'lodash';
import { useIntersectionObserver } from '@vueuse/core';
import StickyAdWrapper from './StickyAdWrapper';
import { activeTabInterval } from '~/modules/common/utils/helpers';
import { createGPTSlot, callPrebid, callCriteo, createPrebidAdUnit } from '~/modules/ads/utils/utils';
import OLoading from '~/modules/common/components/ui/OLoading/OLoading';
import { LANG } from '~/constants/config';
import { isDevMode } from '~/modules/ads/adUtils';

export default defineComponent({
  name: 'gpt',
  components: { OLoading, StickyAdWrapper },
  props: ['unitName', 'targeting'],
  setup(props) {
    const adIntersectionRef = ref(null);
    let stopVisibilityObserver = null;
    const createVisibilityRefreshTimer = (intersectFn) => {
      ({ stop: stopVisibilityObserver } = useIntersectionObserver(
        adIntersectionRef,
        ([{ isIntersecting }]) => {
          if (isIntersecting) {
            intersectFn();
          } else {
            refreshTimer.value?.clear();
          }
        },
        { threshold: 0, rootMargin: '-100px 0px 0px 0px' }
      ));
    };
    const loading = ref(!['appPromo', 'datacollect', 'interstitial'].includes(props.unitName));
    const adMode = inject('adConfig', {});
    const route = useRoute();
    const { $cookies } = useContext();
    const devMode = isDevMode(route, $cookies);
    const { isMobile, isInstagram } = useDevice();
    const mobileKey = computed(() => (isMobile ? 'mobile' : 'desktop'));
    const isViolation = adMode?.value?.decidedMode?.value === 'onlyCriteo';
    const slot = ref(null);
    const randomNumber = ref();
    const selectedAdUnit = computed(() => {
      const device = isMobile.value ? 'mobile' : 'desktop';
      if (LANG !== 'tr' && !adConfig.areas[device][props.unitName]?.i18n?.[LANG]) {
        return null;
      }
      return adConfig.areas[device][props.unitName];
    });
    const adUnitData = selectedAdUnit.value;
    const isPrebidUnit = adUnitData?.prebid && adMode.value.mode.scripts.includes('Prebid');
    const isSticky = computed(() => {
      const device = isMobile.value ? 'mobile' : 'desktop';
      if (isInstagram.value) return false;
      return adMode.value?.mode?.options?.GPT?.stickyUnits?.[device]?.includes(props.unitName);
    });
    const minWidth = ref(null);
    const minHeight = ref(null);
    const clsMode = ref(null);
    const style = computed(() => {
      if (clsMode.value === 'max') {
        return {
          ...(!isSticky.value && { width: (selectedAdUnit.value?.width || minWidth.value) + 'px' }),
          // width: (selectedAdUnit.value?.width || minWidth.value) + 'px',
          height: (selectedAdUnit.value?.height || minHeight.value) + 'px',
        };
      } else if (clsMode.value === 'min') {
        return { minWidth: minWidth.value + 'px', minHeight: minHeight.value + 'px' };
      }
    });
    const refreshTimer = ref(null);
    const calculateMinWidthHeight = () => {
      minWidth.value = 0;
      minHeight.value = 0;
      selectedAdUnit.value?.sizes
        .filter((size) => Array.isArray(size))
        .forEach(([width, height]) => {
          if (clsMode.value === 'max') {
            if (!minWidth.value || !minHeight.value) {
              minWidth.value = width;
              minHeight.value = height;
            }
            if (minWidth.value < width && width !== 1) {
              minWidth.value = width;
            }
            if (minHeight.value < height && height !== 1) {
              minHeight.value = height;
            }
          } else if (clsMode.value === 'min') {
            if (!minWidth.value || !minHeight.value) {
              minWidth.value = width;
              minHeight.value = height;
            }
            if (minWidth.value > width && width !== 1) {
              minWidth.value = width;
            }
            if (minHeight.value > height && height !== 1) {
              minHeight.value = height;
            }
          }
        });
    };
    if (!selectedAdUnit.value?.disableShift) {
      clsMode.value = selectedAdUnit.value?.cls || 'min';
      calculateMinWidthHeight();
    }
    const outerDivId = computed(() => props.unitName + '-container');
    const adUnitId = computed(() => {
      const device = isMobile.value ? 'mobile' : 'desktop';
      if (selectedAdUnit.value?.divId) {
        return selectedAdUnit.value.divId;
      } else {
        return 'div-gpt-' + device + '-' + props.unitName + '-' + randomNumber.value;
      }
    });
    const clearRefreshTimer = () => {
      if (isMobile.value) {
        refreshTimer.value?.clear();
      }
    };

    onUnmounted(() => {
      const googletag = window.googletag;
      googletag.cmd.push(() => {
        stopVisibilityObserver?.();
        refreshTimer.value?.clear();
        googletag.destroySlots([slot.value]);
      });
    });
    onBeforeMount(() => {
      randomNumber.value = uniqueId('uid-');
    });

    if (process.server && isPrebidUnit) {
      adMode.value?.prebidUnits.push(createPrebidAdUnit(adUnitData));
    }

    onMounted(() => {
      window.googletag = window.googletag || { cmd: [] };
      const googletag = window.googletag;

      const refreshProjects = (unitName) => {
        if (window.refreshProjectUnit?.[unitName]) {
          window.refreshProjectUnit[unitName]?.();
          const styleTag = document.getElementById(`${unitName}-projectCSS`);
          if (!styleTag) return;
          document.getElementsByTagName('head')[0].removeChild(styleTag);
        }
      };
      const loadAdUnit = (adUnitData) => {
        const targetings = { ...(adMode?.value && adMode.value.targetings) };
        if (props.targeting) {
          Object.keys(props.targeting).forEach((key) => {
            targetings.hasOwnProperty(key) ? targetings[key]?.push(props.targeting[key]) : (targetings[key] = props.targeting[key]);
          });
        }
        if (isPrebidUnit) {
          googletag.cmd.push(async function () {
            slot.value = createGPTSlot(adUnitData, adUnitId.value, targetings);
            await callPrebid(slot.value);
            loading.value = false;
            if (adUnitData.refreshTimer) {
              if (adUnitData.visibleRefresh) {
                createVisibilityRefreshTimer(() => {
                  refreshTimer.value = activeTabInterval(() => callPrebid(slot.value), adUnitData.refreshTimer * 1000);
                });
              } else {
                refreshTimer.value = activeTabInterval(() => {
                  refreshProjects(props.unitName);
                  callPrebid(slot.value);
                }, adUnitData.refreshTimer * 1000);
              }
            }
          });
        } else if (adMode.value?.mode?.options?.GPT?.withCriteo && adMode.value.decidedMode.value !== 'sponsored') {
          googletag.cmd.push(function () {
            slot.value = createGPTSlot(adUnitData, adUnitId.value, targetings);
          });
          googletag.cmd.push(async function () {
            await callCriteo(slot.value);
            loading.value = false;
            if (adUnitData.refreshTimer) {
              if (adUnitData.visibleRefresh) {
                createVisibilityRefreshTimer(() => {
                  refreshTimer.value = activeTabInterval(() => {
                    googletag.pubads().refresh([slot.value]);
                  }, adUnitData.refreshTimer * 1000);
                });
              } else {
                refreshTimer.value = activeTabInterval(() => {
                  googletag.pubads().refresh([slot.value]);
                }, adUnitData.refreshTimer * 1000);
              }
            }
          });
        } else {
          googletag.cmd.push(function () {
            slot.value = createGPTSlot(adUnitData, adUnitId.value, targetings);
            googletag.pubads().refresh([slot.value]);
            loading.value = false;
            if (adMode?.value?.decidedMode?.value !== 'sponsored' && adUnitData.refreshTimer) {
              if (adUnitData.visibleRefresh) {
                createVisibilityRefreshTimer(() => {
                  refreshTimer.value = activeTabInterval(() => {
                    googletag.pubads().refresh([slot.value]);
                  }, adUnitData.refreshTimer * 1000);
                });
              } else {
                refreshTimer.value = activeTabInterval(() => {
                  googletag.pubads().refresh([slot.value]);
                }, adUnitData.refreshTimer * 1000);
              }
            }
          });
        }
      };

      const loadAdUnitWrapper = (adUnitData) => {
        // Violation protect
        if (isViolation) return;
        if (adUnitData?.referrer && route?.value?.query?.noreferrer === undefined) {
          const referrer = typeof document.referrer !== 'undefined' ? document.referrer : '';
          const includes = adUnitData.referrer.include && adUnitData.referrer.include.some((value) => referrer.includes(value));
          const excludes = adUnitData.referrer.exclude && adUnitData.referrer.exclude.some((value) => referrer.includes(value));
          if (includes && !excludes) {
            loadAdUnit(adUnitData);
          }
        } else if (adUnitData?.queryString) {
          const queries = route?.value?.query;
          const includes = adUnitData.queryString.includes.some((i) => Object.entries(i).some(([key, v]) => queries[key] === v));
          const excludes = adUnitData.queryString.excludes.some((i) => Object.entries(i).some(([key, v]) => queries[key] === v));
          includes && !excludes && loadAdUnit(adUnitData);
        } else {
          loadAdUnit(adUnitData);
        }
      };
      watch(isMobile, () => {
        const adUnitData = selectedAdUnit.value;
        if (adUnitData) {
          if (isPrebidUnit) {
            window.pbjs.que.push(function () {
              window.pbjs.addAdUnits([createPrebidAdUnit(adUnitData)]);
            });
          }
          googletag.cmd.push(() => {
            if (slot.value) {
              stopVisibilityObserver?.();
              googletag.destroySlots([slot.value]);
              refreshTimer.value?.clear();
            }
            calculateMinWidthHeight();
            loadAdUnitWrapper(adUnitData);
          });
        } else if (slot.value) {
          stopVisibilityObserver?.();
          googletag.destroySlots([slot.value]);
          refreshTimer.value?.clear();
        }
      });
      if (adUnitData) {
        loadAdUnitWrapper(adUnitData);
      }
    });
    return {
      selectedAdUnit,
      outerDivId,
      adUnitId,
      mobileKey,
      minWidth,
      minHeight,
      adMode,
      loading,
      style,
      devMode,
      isSticky,
      slot,
      clearRefreshTimer,
      adIntersectionRef,
    };
  },
});
</script>
